import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { 
  Sparkles, FileText, BookOpen, Users, Info, 
  CheckCircle, Clock, AlertTriangle, Zap 
} from 'lucide-react';
import { toast } from 'sonner';
import { AppProfileContext } from '@/contexts/AppProfileContext';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SelfMarketingEngine() {
  const { user } = useContext(AuthContext);
  const { appProfiles, activeProfileId, selectProfile, loading: profilesLoading } = useContext(AppProfileContext);
  const [drafts, setDrafts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState(null);

  useEffect(() => {
    if (!user?.is_internal_admin || profilesLoading) return;
    setLoading(true);
    loadData();
  }, [user, profilesLoading, activeProfileId]);

  const loadData = async () => {
    try {
      // Load autopilot submissions as potential sources
      const subRes = await fetch(`${API}/autopilot/submissions`, {
        credentials: 'include'
      });
      
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubmissions(subData.submissions || []);
      }

      // Load existing drafts (would come from a drafts API)
      // For now, we'll use localStorage keyed by App Profile
      const key = `self_marketing_drafts_${activeProfileId || 'default-app-profile'}`;
      const savedDrafts = localStorage.getItem(key);
      if (savedDrafts) {
        setDrafts(JSON.parse(savedDrafts));
      } else {
        setDrafts([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const persistDrafts = (updatedDrafts) => {
    const key = `self_marketing_drafts_${activeProfileId || 'default-app-profile'}`;
    localStorage.setItem(key, JSON.stringify(updatedDrafts));
  };

  const generateDraft = async (source, type) => {
    setGeneratingFor(`${source.id}-${type}`);
    
    // Simulate draft generation (no actual AI processing)
    setTimeout(() => {
      const newDraft = {
        id: `draft_${Date.now()}`,
        type: type,
        title: type === 'help_center' 
          ? `How to: ${source.content_title || 'Content Submission'}`
          : type === 'feature_explanation'
          ? `Understanding: ${source.content_title}`
          : `Onboarding: Getting Started with ${source.platforms.join(', ')}`,
        content: type === 'help_center'
          ? `# How to Submit Content via Assisted Autopilot\n\n1. Navigate to CREATE → Assisted Autopilot\n2. Choose your content type (link or upload)\n3. Select target platforms\n4. Set your goal and submit\n\nYour content will be reviewed and executed by our team.`
          : type === 'feature_explanation'
          ? `# Assisted Autopilot: ${source.content_title}\n\nThis feature allows you to submit content for execution across multiple platforms. Our team reviews and processes your submissions to ensure quality and consistency.\n\n**Platforms:** ${source.platforms.join(', ')}\n**Goal:** ${source.goal}`
          : `# Welcome to Assisted Autopilot\n\nGet started by submitting your first content piece. We'll handle the execution while you focus on creation.\n\n**Supported Platforms:**\n- YouTube\n- TikTok\n- Instagram\n- Facebook`,
        source_type: 'autopilot_submission',
        source_id: source.id,
        status: 'draft',
        created_at: new Date().toISOString(),
        approved: false
      };

      const updatedDrafts = [newDraft, ...drafts];
      setDrafts(updatedDrafts);
      persistDrafts(updatedDrafts);
      
      toast.success(`${type === 'help_center' ? 'Help Center' : type === 'feature_explanation' ? 'Feature' : 'Onboarding'} draft generated`);
      setGeneratingFor(null);
    }, 1500);
  };

  const approveDraft = (draftId) => {
    const updatedDrafts = drafts.map(d => 
      d.id === draftId ? { ...d, approved: true, status: 'approved' } : d
    );
    setDrafts(updatedDrafts);
    persistDrafts(updatedDrafts);
    toast.success('Draft approved - Ready for publishing (manual step)');
  };

  const deleteDraft = (draftId) => {
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    setDrafts(updatedDrafts);
    persistDrafts(updatedDrafts);
    toast.success('Draft deleted');
  };

  if (!user?.is_internal_admin) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-400">
            Admin access required
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (profilesLoading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }

  if (!activeProfileId && appProfiles.length === 0) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <AlertDescription className="text-yellow-400">
            No App Profiles available. Create one in Admin → App Profile to enable Self-Marketing Engine context.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-pink-500" />
          Self-Marketing Engine
        </h1>
        <p className="text-[#8A93A6]">Admin Tools - Generate marketing drafts (approval required) per App Profile</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs uppercase text-[#8A93A6]">App Profile Context</label>
            <div className="mt-1 flex items-center gap-3">
              <select
                className="bg-[#1C1F26] border border-white/10 rounded-lg px-3 py-2 text-white w-full sm:w-auto"
                value={activeProfileId || ''}
                onChange={(e) => selectProfile(e.target.value)}
              >
                {appProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name} {profile.is_default ? '(Default)' : ''}
                  </option>
                ))}
              </select>
              <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                Scoped to selected App Profile
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Draft-Only Mode:</strong> All content generated here requires manual admin approval before publishing. 
          No auto-publishing. Sources include Autopilot submissions, status changes, and support feedback.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Content Sources</h2>
          
          {/* Autopilot Submissions */}
          <Card className="glass mb-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-5 w-5 text-pink-500" />
                Autopilot Submissions
              </CardTitle>
              <CardDescription>Generate marketing content from user submissions</CardDescription>
            </CardHeader>
            <CardContent>
              {submissions.length === 0 ? (
                <p className="text-sm text-[#8A93A6] text-center py-4">No submissions yet</p>
              ) : (
                <div className="space-y-3">
                  {submissions.slice(0, 3).map((sub) => (
                    <div key={sub.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-sm font-medium text-white mb-2">
                        {sub.content_title}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateDraft(sub, 'help_center')}
                          disabled={generatingFor === `${sub.id}-help_center`}
                        >
                          <BookOpen className="h-3 w-3 mr-1" />
                          {generatingFor === `${sub.id}-help_center` ? 'Generating...' : 'Help Article'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateDraft(sub, 'feature_explanation')}
                          disabled={generatingFor === `${sub.id}-feature_explanation`}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          {generatingFor === `${sub.id}-feature_explanation` ? 'Generating...' : 'Feature Doc'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Support Feedback */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Support Feedback
              </CardTitle>
              <CardDescription>Common questions and feature requests</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#8A93A6] text-center py-4">
                Connect support system to enable
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Generated Drafts */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Generated Drafts</h2>
          
          {drafts.length === 0 ? (
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
                  <p className="text-[#8A93A6]">No drafts generated yet</p>
                  <p className="text-sm text-[#8A93A6] mt-2">Generate from sources on the left</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {drafts.map((draft) => (
                <Card key={draft.id} className="glass">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base mb-2">{draft.title}</CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={draft.approved ? 'border-green-500 text-green-500' : 'border-yellow-500 text-yellow-500'}
                          >
                            {draft.approved ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                Draft
                              </>
                            )}
                          </Badge>
                          <Badge variant="outline" className="capitalize text-xs">
                            {draft.type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={draft.content}
                      readOnly
                      className="glass mb-3 min-h-[150px]"
                    />
                    <div className="flex gap-2">
                      {!draft.approved && (
                        <Button
                          size="sm"
                          onClick={() => approveDraft(draft.id)}
                        >
                          Approve Draft
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteDraft(draft.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
